import { useSettingStatic } from "@composables/settings"
import { usePreferredDark, useStorage } from "@vueuse/core"
import { App, Ref, computed, reactive, watch } from "vue"

import type { HoppBgColor } from "~/newstore/settings"
import { isThemeLockedForChange, getLockedTheme } from "~/newstore/settings"
import { PersistenceService } from "~/services/persistence"
import { HoppModule } from "."
import { getService } from "./dioc"

export type HoppColorMode = {
  preference: HoppBgColor
  value: Readonly<Exclude<HoppBgColor, "system">>
}

const persistenceService = getService(PersistenceService)

const applyColorMode = (app: App) => {
  const [settingPref] = useSettingStatic("BG_COLOR")

  const currentLocalPreference = useStorage<HoppBgColor>(
    "nuxt-color-mode",
    settingPref.value, // 使用设置存储中的当前值作为默认值
    persistenceService.hoppLocalConfigStorage,
    {
      listenToStorageChanges: true,
    }
  )

  const systemPrefersDark = usePreferredDark()

  const selection = computed<Exclude<HoppBgColor, "system">>(() => {
    if (currentLocalPreference.value === "system") {
      return systemPrefersDark.value ? "dark" : "light"
    }
    return currentLocalPreference.value
  })

  const setDocumentThemeClass = (model: Exclude<HoppBgColor, "system">) => {
    const classValue = model === "black" ? "black dark" : model
    document.documentElement.setAttribute("class", classValue)
  }
  watch(
    selection,
    (newSelection) => {
      // 如果主题被锁定，只使用锁定的主题
      if (isThemeLockedForChange()) {
        const locked = getLockedTheme()
        if (locked && locked !== newSelection) {
          console.log(
            `[Theming] 主题已锁定为 ${locked}，忽略 selection 变化: ${newSelection}`
          )
          setDocumentThemeClass(locked as Exclude<HoppBgColor, "system">)
          return
        }
      }

      setDocumentThemeClass(newSelection)
    },
    { immediate: true }
  )

  watch(
    settingPref,
    (newPref) => {
      // 如果主题被锁定，忽略 settingPref 的变化
      if (isThemeLockedForChange()) {
        const locked = getLockedTheme()
        console.log(
          `[Theming] 主题已锁定为 ${locked}，忽略 settingPref 变化: ${newPref}`
        )
        // 强制保持锁定的主题
        if (locked) {
          setDocumentThemeClass(locked as Exclude<HoppBgColor, "system">)
        }
        return
      }

      currentLocalPreference.value = newPref
      setDocumentThemeClass(selection.value)
    },
    { immediate: true }
  )

  const exposed: HoppColorMode = reactive({
    preference: currentLocalPreference,
    // Marking as readonly to not allow writes to this ref
    value: selection as Readonly<Ref<Exclude<HoppBgColor, "system">>>,
  })

  app.provide("colorMode", exposed)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applyAccentColor = (_app: App) => {
  const [pref] = useSettingStatic("THEME_COLOR")

  watch(
    pref,
    (newPref) => {
      document.documentElement.setAttribute("data-accent", newPref)
    },
    { immediate: true }
  )
}

export default <HoppModule>{
  onVueAppInit(app) {
    applyColorMode(app)
    applyAccentColor(app)
  },
}
